import type { ReactNode } from 'react';
import { Recovery as Words } from '@beyond-js/ui';
import { Callout, ChangeOutcome, TechnicalDetails } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';

/** Whether the browser itself is offline, so no product is named as the one that did not answer */
function offline(): boolean {
	return globalThis.navigator?.onLine === false;
}

/**
 * A failure or a refusal, said once, with its "Technical details" (D43): the request's identifier when
 * the service answered with one, and the time, copyable for whoever investigates (ACC-13).
 *
 * The words come from the refusal's own catalog key (`Refusal.key`), so a screen never invents its own
 * wording for a code; `title` replaces them where the screen knows better what failed. A refusal that
 * asks to wait says how long (`state.limited`). An outage (`Refusal.unreachable`) is drawn in the warning
 * tone of the family's `Unavailable`, never as a failure or an alert (D40, D132, D133, D186); `tone`
 * overrides it.
 *
 * A change that met an outage is the family's `ChangeOutcome` (D186, `@beyond-js/ui` 0.33.0):
 *
 * - Answered as unavailable, or never sent: who did not answer ("Beyond Accounts did not answer", "Beyond
 *   Accounts could not be reached", or "You're offline" while the browser is) and "Nothing was changed.",
 *   with `again`, the person's own Try again, only where no action of the change's own stays in view (a
 *   form's submit, a dialog's accept, a row's button): a failure never restates its button. It is never
 *   sent again by itself.
 * - No answer: "The change may have been made.", who did not answer and "Check before trying again.", with
 *   Check this page, which reads again what the change touched (`check`, else who is here and their
 *   organizations), and no Try again. A check that opens another page names that page instead
 *   (`{ label, run }`, such as "Check your tokens" after a token's creation).
 *
 * @param again Sends the change once more, at the person's request; offered only when it was not made
 * @param check Reads again what a change with no answer touched, or opens where it is said, with its own label
 */
export function Problem({
	error,
	title,
	tone,
	actions,
	again,
	check,
	children
}: {
	error: unknown;
	title?: string;
	tone?: 'danger' | 'warning';
	actions?: ReactNode;
	again?: () => unknown;
	check?: (() => unknown) | { label: string; run: () => unknown };
	children?: ReactNode;
}) {
	const { text, language, refresh } = usePresence();
	const refusal = Refusal.is(error) ? error : new Refusal('INTERNAL', String((error as Error)?.message ?? error));
	const evidence = { text: `${refusal.code}: ${refusal.message}`, request: refusal.request ?? null, time: refusal.at };
	// Who did not answer, unless the browser itself is offline
	const said = (key: string) => (offline() ? String(Words.labels[language === 'es' ? 'es' : 'en'].offline) : text(key));

	if (!title && (refusal.unknown || ['state.unavailable', 'state.unsent'].includes(refusal.key))) {
		// Technical details in the interface's language, which the outcome hands to its disclosure
		const details = { ...evidence, locale: language, labels: TechnicalDetails.labels?.[language] } as typeof evidence;
		const words = ChangeOutcome.labels[language] ?? ChangeOutcome.labels.en;
		if (refusal.unknown) {
			const way = typeof check === 'object' ? { label: check.label, onSelect: () => void check.run() } : { label: text('state.check'), onSelect: () => void (check ?? refresh)() };
			return <ChangeOutcome outcome="unknown" cause={said('outage.accounts')} check={way} details={details} labels={words} />;
		}
		const cause = said(refusal.code === 'UNSENT' ? 'outage.unreached' : 'outage.accounts');
		return <ChangeOutcome outcome="unavailable" cause={cause} details={details} onAgain={again ?? null} labels={words} />;
	}
	return (
		<Callout tone={tone ?? (refusal.unreachable ? 'warning' : 'danger')} live title={title ?? text(refusal.key, refusal.values)} actions={actions}
			details={{ ...evidence, locale: language, labels: TechnicalDetails.labels?.[language] }}>
			{children}
		</Callout>
	);
}
